// apps/admin/src/pages/OnboardingStatusPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { doc, onSnapshot, updateDoc, collection, query, where, limit } from 'firebase/firestore';
import {
  CheckCircle, Clock, AlertCircle, Mail, Building,
  Users, Database, CreditCard, FileCheck, Server,
  Zap, Shield
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { ZipUploadMonitor } from '../components/ZipUploadMonitor';

interface ProcessingStep {
  id: string;
  label: string;
  icon: React.ReactNode;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  value?: string | number;
  percentage: number;
}

const OnboardingStatusPage: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [overallProgress, setOverallProgress] = useState(0);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState<string>('Calculating...');
  const [companyData, setCompanyData] = useState<any>(null);
  const startTimeRef = useRef<Date>(new Date());
  const [activeUpload, setActiveUpload] = useState<{
    companyId: string;
    sourceDocId: string;
  } | null>(null);
  
  const [steps, setSteps] = useState<ProcessingStep[]>([
    { id: 'company_setup', label: 'Company Setup', icon: <Building className="w-4 h-4" />, status: 'processing', percentage: 0 },
    { id: 'payment_verification', label: 'Payment Verification', icon: <CreditCard className="w-4 h-4" />, status: 'pending', percentage: 0 },
    { id: 'office_creation', label: 'Office Creation', icon: <Building className="w-4 h-4" />, status: 'pending', value: 0, percentage: 0 },
    { id: 'broker_import', label: 'Broker Import', icon: <Users className="w-4 h-4" />, status: 'pending', value: 0, percentage: 0 },
    { id: 'data_processing', label: 'Commission Data', icon: <Database className="w-4 h-4" />, status: 'pending', value: 0, percentage: 0 },
    { id: 'template_configuration', label: 'Template Setup', icon: <FileCheck className="w-4 h-4" />, status: 'pending', percentage: 0 },
    { id: 'security_setup', label: 'Security & RBAC', icon: <Shield className="w-4 h-4" />, status: 'pending', percentage: 0 },
    { id: 'email_dispatch', label: 'Welcome Emails', icon: <Mail className="w-4 h-4" />, status: 'pending', value: 'Pending', percentage: 0 },
    { id: 'final_verification', label: 'Final Verification', icon: <Zap className="w-4 h-4" />, status: 'pending', percentage: 0 }
  ]);

  useEffect(() => {
    if (!companyId) return;

    // Listen to BOTH onboarding_status AND companies documents
    const unsubscribeStatus = onSnapshot(
      doc(db, 'onboarding_status', companyId),
      (snapshot) => {
        if (snapshot.exists()) {
          const statusData = snapshot.data();

          // Update company name from onboarding_status
          if (statusData.companyName) {
            setCompanyData((prev: any) => ({ ...prev, companyName: statusData.companyName }));
          }

          // Map status stages to UI format
          const mappedSteps: ProcessingStep[] = statusData.stages.map((stage: any) => {
            // Determine icon based on stage id
            let icon = <Server className="w-4 h-4" />;
            switch (stage.id) {
              case 'company_setup':
                icon = <Building className="w-4 h-4" />;
                break;
              case 'payment_verification':
                icon = <CreditCard className="w-4 h-4" />;
                break;
              case 'office_creation':
                icon = <Building className="w-4 h-4" />;
                break;
              case 'broker_import':
                icon = <Users className="w-4 h-4" />;
                break;
              case 'commission_data':
                icon = <Database className="w-4 h-4" />;
                break;
              case 'template_setup':
                icon = <FileCheck className="w-4 h-4" />;
                break;
              case 'security_rbac':
                icon = <Shield className="w-4 h-4" />;
                break;
              case 'welcome_emails':
                icon = <Mail className="w-4 h-4" />;
                break;
              case 'final_verification':
                icon = <Zap className="w-4 h-4" />;
                break;
            }

            // Format value as "current/total" or just current count
            let displayValue: string | number | undefined = undefined;
            if (stage.progress.total > 0) {
              displayValue = `${stage.progress.current}/${stage.progress.total}`;
            } else if (stage.progress.current > 0) {
              displayValue = stage.progress.current;
            }

            return {
              id: stage.id,
              label: stage.name,
              icon,
              status: stage.status,
              value: displayValue,
              percentage: stage.progress.percentage,
            };
          });

          setSteps(mappedSteps);
          setOverallProgress(statusData.overallProgress);

          // Calculate estimated time
          if (statusData.status === 'processing' && statusData.metrics?.startedAt) {
            const startTime = statusData.metrics.startedAt.toMillis();
            const elapsed = Date.now() - startTime;
            const progress = statusData.overallProgress;

            if (progress > 5) {
              const totalEstimated = elapsed / (progress / 100);
              const remaining = totalEstimated - elapsed;
              const minutes = Math.ceil(remaining / 60000);

              if (minutes <= 1) {
                setEstimatedTimeRemaining('Less than a minute');
              } else {
                setEstimatedTimeRemaining(`~${minutes} minutes`);
              }
            } else {
              setEstimatedTimeRemaining('Calculating...');
            }
          } else if (statusData.status === 'completed') {
            setEstimatedTimeRemaining('Complete!');
          } else {
            setEstimatedTimeRemaining('Waiting to start...');
          }
        } else {
          // Document doesn't exist YET - this is normal during onboarding initialization
          // The listener will fire again once the document is created by the backend
          console.log('[OnboardingStatus] Waiting for onboarding_status document to be created...');
          setEstimatedTimeRemaining('Initializing...');
          setOverallProgress(0);

          // Keep listening - document will be created shortly
          // Note: We don't fall back to company doc because onboarding_status is the source of truth
        }
      },
      (error) => {
        console.error('Error listening to onboarding status:', error);
        // Fallback to company document on error
      }
    );

    // ALSO listen to company document for payment status
    const unsubscribeCompany = onSnapshot(
      doc(db, 'companies', companyId),
      (companySnapshot) => {
        if (companySnapshot.exists()) {
          const companyDoc = companySnapshot.data();
          // Update payment status from company document
          setCompanyData((prev: any) => ({
            ...prev,
            onboarding: companyDoc.onboarding
          }));
        }
      }
    );

    // ALSO listen for active commission_sources uploads for this company
    const commissionsRef = collection(db, `companies/${companyId}/commission_sources`);
    const activeUploadsQuery = query(
      commissionsRef,
      where('status', 'in', ['processing', 'queued']),
      limit(1)
    );

    const unsubscribeUploads = onSnapshot(
      activeUploadsQuery,
      (snapshot) => {
        if (!snapshot.empty) {
          const uploadDoc = snapshot.docs[0];
          setActiveUpload({
            companyId: companyId,
            sourceDocId: uploadDoc.id
          });
        } else {
          setActiveUpload(null);
        }
      },
      (error) => {
        console.error('Error listening to commission_sources:', error);
      }
    );

    return () => {
      unsubscribeStatus();
      unsubscribeCompany();
      unsubscribeUploads();
    };
  }, [companyId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900/50 via-gray-900 to-fuchsia-900/50 p-8">
      <div className="max-w-5xl mx-auto">
        <div className="bg-black/30 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-4">
              {overallProgress === 100 ? '✅ Onboarding Complete!' : 'Onboarding in Progress'}
            </h1>
            <p className="text-gray-400 mb-2">Company: {companyData?.companyName || 'Loading...'}</p>
            <p className="text-gray-500 text-sm">ID: {companyId}</p>
            
            {/* Main Progress Bar */}
            <div className="mt-6 relative">
              <div className="h-8 bg-gray-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-fuchsia-500 rounded-full transition-all duration-1000 ease-out relative"
                  style={{ width: `${overallProgress}%` }}
                >
                  <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                </div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-lg font-bold text-white drop-shadow-lg">{overallProgress}%</span>
              </div>
            </div>
            
            <div className="mt-3 text-sm text-gray-400">
              {overallProgress < 100 && `Estimated time remaining: ${estimatedTimeRemaining}`}
            </div>
          </div>

          {/* Real-Time Upload Monitor */}
          {activeUpload && (
            <div className="mb-8">
              <ZipUploadMonitor
                companyId={activeUpload.companyId}
                sourceDocId={activeUpload.sourceDocId}
                onComplete={() => {
                  // Optionally clear monitor after a delay
                  setTimeout(() => setActiveUpload(null), 5000);
                }}
              />
            </div>
          )}

          {/* Processing Steps */}
          <div className="space-y-3">
            {steps.map((step, index) => (
              <div key={step.id} className={`bg-slate-800/50 rounded-lg p-4 transition-all duration-500 ${
                step.status === 'processing' ? 'ring-2 ring-blue-500/50 animate-pulse' : ''
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg transition-all duration-300 ${
                      step.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                      step.status === 'processing' ? 'bg-blue-500/20 text-blue-400' :
                      step.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                      'bg-gray-700 text-gray-400'
                    }`}>
                      {step.icon}
                    </div>
                    <div>
                      <p className="text-white font-medium">{step.label}</p>
                      {step.value !== undefined && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          {typeof step.value === 'number' ? `${step.value} processed` : step.value}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-32 h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all duration-500 ${
                          step.status === 'completed' ? 'bg-green-500' :
                          step.status === 'processing' ? 'bg-blue-500' :
                          'bg-gray-600'
                        }`}
                        style={{ width: `${step.percentage}%` }}
                      />
                    </div>
                    {step.status === 'completed' ? (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    ) : step.status === 'processing' ? (
                      <Clock className="w-5 h-5 text-blue-400 animate-spin" />
                    ) : step.status === 'failed' ? (
                      <AlertCircle className="w-5 h-5 text-red-400" />
                    ) : (
                      <div className="w-5 h-5 border-2 border-gray-600 rounded-full" />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          {overallProgress === 100 && (
            <div className="mt-8 p-6 bg-green-500/10 border border-green-500/30 rounded-lg">
              <h3 className="text-lg font-semibold text-green-400 mb-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Setup Complete!
              </h3>
              <p className="text-gray-300 mb-4">
                The company has been successfully onboarded. All users will receive welcome emails.
              </p>
              <div className="flex gap-4">
                <button
                  onClick={() => navigate(`/companies/${companyId}`)}
                  className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all font-medium"
                >
                  View Company Details
                </button>
                <button
                  onClick={() => navigate('/companies/onboard')}
                  className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-all font-medium"
                >
                  Onboard Another Company
                </button>
              </div>
            </div>
          )}

          {/* Payment Warning */}
          {!companyData?.onboarding?.paymentReceived && overallProgress < 100 && (
            <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <p className="text-yellow-400 text-sm flex items-center gap-2 mb-3">
                <AlertCircle className="w-4 h-4" />
                File processing is paused. Payment must be marked as received to continue.
              </p>
              <button
                onClick={async () => {
                  if (!companyId) return;
                  try {
                    await updateDoc(doc(db, 'companies', companyId), {
                      'onboarding.paymentReceived': true,
                      'onboarding.paymentDate': new Date()
                    });
                    toast.success('Payment marked as received! Processing will resume.');
                  } catch (error: any) {
                    toast.error('Failed to update payment status: ' + error.message);
                  }
                }}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-all flex items-center gap-2"
              >
                <CreditCard className="w-4 h-4" />
                Mark Payment as Received
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingStatusPage;